import { FormControlLabel, Switch } from "@mui/material";
import { FC, useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import {
  editFormDataAF,
  EditFormMode,
  IRideDto,
  IStationRouteDto,
  StationRouteFormFields,
  unique,
} from "@ryde/shared";

interface IStationRouteTabsFixedControls {
  stationRouteId: number;
  isFormDisabled?: boolean;
  rideDto: Partial<IRideDto> | undefined;
}

export const StationRouteTabsFixedControls: FC<
  IStationRouteTabsFixedControls
> = ({ stationRouteId, isFormDisabled, rideDto }) => {
  const { t } = useTranslation();
  const {
    setValue,
    watch,
    formState: { isDirty },
  } = useFormContext<Partial<IStationRouteDto>>();

  const stationRouteDto = useRecoilValue<Partial<IStationRouteDto> | undefined>(
    editFormDataAF(["stationRoute", EditFormMode.UnboundDrawer]),
  );

  const excludedStationRouteIds = rideDto?.metadata?.excludedStationRouteIds;

  useEffect(() => {
    if (!isDirty) {
      setValue(
        StationRouteFormFields.excludedStationRouteIds,
        excludedStationRouteIds || [],
        {
          shouldDirty: true,
        },
      );
    }
  }, [excludedStationRouteIds, isDirty, setValue]);

  const watchedExcludedIdsValue = watch(
    StationRouteFormFields.excludedStationRouteIds,
  );

  const onExcludedCurrentRideChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const checkedValue = event.target.checked || false;
    const computedFieldValue = checkedValue
      ? (excludedStationRouteIds || []).filter(id => id !== stationRouteId)
      : unique([...(excludedStationRouteIds || []), stationRouteId]);

    setValue(
      StationRouteFormFields.excludedStationRouteIds,
      computedFieldValue,
      {
        shouldValidate: true,
        shouldDirty: true,
      },
    );
  };

  const initialIsCurrentRouteExcludedValue = useMemo(() => {
    if (!excludedStationRouteIds?.length) {
      return false;
    }
    const excludedStationRouteIdsSet = new Set(excludedStationRouteIds);
    return excludedStationRouteIdsSet.has(stationRouteId);
  }, [excludedStationRouteIds, stationRouteId]);
  const updatedIsCurrentRouteExcludedValue = useMemo(() => {
    if (!watchedExcludedIdsValue?.length) {
      return false;
    }
    const watchedExcludedIdsValueSet = new Set(watchedExcludedIdsValue);
    return watchedExcludedIdsValueSet.has(stationRouteId);
  }, [watchedExcludedIdsValue, stationRouteId]);

  const isCurrentStationRouteExcluded =
    watchedExcludedIdsValue === undefined
      ? initialIsCurrentRouteExcludedValue
      : updatedIsCurrentRouteExcludedValue;

  return (
    Boolean(stationRouteDto?.id && stationRouteId) && (
      <FormControlLabel
        disabled={isFormDisabled}
        control={
          <Switch
            data-e2e-id={"exclude_current_ride"}
            checked={!isCurrentStationRouteExcluded}
            onChange={onExcludedCurrentRideChange}
          />
        }
        label={
          isCurrentStationRouteExcluded
            ? t("stationRoute.formLabel.reactivatePath")
            : t("stationRoute.formLabel.activeForCurrentRide")
        }
      />
    )
  );
};
